import { useGetList, type Identifier } from "ra-core";
import type { Task } from "@/components/atomic-crm/types";

/** A task with the optional links of public.tasks (a dialer callback is often company-only). */
export type RelatedTask = Task & {
  deal_id?: Identifier | null;
  company_id?: Identifier | null;
};

export type RelatedTasksParams = {
  dealIds?: readonly Identifier[];
  contactIds?: readonly Identifier[];
  companyId?: Identifier | null;
};

const LIST = {
  sort: { field: "due_date", order: "ASC" as const },
  pagination: { page: 1, perPage: 1000 },
};

const inFilter = (ids: readonly Identifier[]) => `(${ids.join(",")})`;

/**
 * Tasks linked to any of the given deals, contacts or compagnie, each once, by due date.
 * Three plain getList queries (no `@or`: the demo provider only supports it for full-text search);
 * a query with no id is skipped. The keys stay under ["tasks", "getList"], so creating, checking or
 * editing a task refreshes them like any task list.
 */
export const useRelatedTasks = ({
  dealIds = [],
  contactIds = [],
  companyId = null,
}: RelatedTasksParams) => {
  const byDeal = useGetList<RelatedTask>(
    "tasks",
    { ...LIST, filter: { "deal_id@in": inFilter(dealIds) } },
    { enabled: dealIds.length > 0 },
  );
  const byContact = useGetList<RelatedTask>(
    "tasks",
    { ...LIST, filter: { "contact_id@in": inFilter(contactIds) } },
    { enabled: contactIds.length > 0 },
  );
  const byCompany = useGetList<RelatedTask>(
    "tasks",
    { ...LIST, filter: { company_id: companyId } },
    { enabled: companyId != null },
  );

  const queries = [
    { query: byDeal, enabled: dealIds.length > 0 },
    { query: byContact, enabled: contactIds.length > 0 },
    { query: byCompany, enabled: companyId != null },
  ].filter((q) => q.enabled);

  const merged = new Map<string, RelatedTask>();
  for (const { query } of queries) {
    for (const t of query.data ?? []) merged.set(String(t.id), t);
  }
  const data = [...merged.values()].sort((a, b) =>
    a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0,
  );

  return {
    data,
    isPending: queries.some(({ query }) => query.isPending),
    error: queries.find(({ query }) => query.error)?.query.error ?? null,
    refetch: () => {
      for (const { query } of queries) void query.refetch();
    },
  };
};
